/**
 * jv-party-inspo-upload.js — Self-contained inspiration image uploader for bridal party members.
 *
 * Usage:
 *   jvPartyInspoUpload_init({
 *     containerEl:       HTMLElement,   // where to render the uploader
 *     bridgeCall:        Function,      // jvBridge.call or equivalent: (route, payload) => Promise
 *     memberContext:     'bride' | 'partyMember',
 *     targetMemberName:  string,
 *     targetMemberRole:  string,
 *     partyToken:        string|null,   // required when memberContext === 'partyMember'
 *     onSuccess:         Function       // callback(category, fileUrl, savedAs)
 *   })
 *
 * No external dependencies. No emojis. Design tokens from CSS custom properties.
 */
(function () {
  'use strict';

  var ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/heic', 'image/webp'];
  var MAX_BYTES = 10 * 1024 * 1024;

  /* ── SVG icons (16×16, light stroke) ────────────────────────────── */
  var SVG_ADD = '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="8" y1="3" x2="8" y2="13"/><line x1="3" y1="8" x2="13" y2="8"/></svg>';
  var SVG_TRASH = '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="2 4 14 4"/><path d="M5 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1"/><path d="M6 7v5m4-5v5"/><path d="M3 4l1 9a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-9"/></svg>';
  var SVG_CHECK = '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="2.5 8 6.5 12 13.5 4.5"/></svg>';
  var SVG_WARN = '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 2L14.5 13H1.5z"/><line x1="8" y1="7" x2="8" y2="10"/><circle cx="8" cy="12" r="0.5" fill="currentColor"/></svg>';

  /* ── Styles (scoped to .jv-pinspo) ──────────────────────────────── */
  var STYLES = [
    '.jv-pinspo{font-family:var(--font-body-family,"Plus Jakarta Sans",sans-serif);color:var(--brand-text,#2f2524);}',
    '.jv-pinspo__zone{border:1.5px dashed var(--brand-primary,#c99591);border-radius:var(--radius-medium,1rem);background:var(--brand-bg-soft,#f6f1ee);padding:1.25rem;text-align:center;cursor:pointer;transition:border-color .2s,background .2s;}',
    '.jv-pinspo__zone:hover,.jv-pinspo__zone.is-drag-over{border-color:var(--brand-primary-deep,#a9706c);background:#ede7e4;}',
    '.jv-pinspo__zone-label{font-size:.875rem;line-height:1.5;margin:0;}',
    '.jv-pinspo__zone-sub{font-size:.75rem;color:var(--brand-primary-deep,#a9706c);margin:.25rem 0 0;}',
    '.jv-pinspo__pick-btn{display:inline-flex;align-items:center;gap:.375rem;margin-top:.625rem;padding:.5rem 1rem;border-radius:var(--radius-pill,3rem);background:var(--brand-primary,#c99591);color:#fff;font-family:var(--font-heading-family,"Raleway",sans-serif);font-weight:600;font-size:.8125rem;letter-spacing:.03em;border:none;cursor:pointer;transition:background .15s;}',
    '.jv-pinspo__pick-btn:hover{background:var(--brand-primary-deep,#a9706c);}',
    '.jv-pinspo__list{list-style:none;margin:.75rem 0 0;padding:0;display:flex;flex-direction:column;gap:.5rem;}',
    '.jv-pinspo__item{display:flex;align-items:center;gap:.5rem;background:#fff;border-radius:var(--radius-medium,1rem);padding:.5rem .625rem;box-shadow:var(--shadow-card,0 4px 24px rgba(47,37,36,.06));}',
    '.jv-pinspo__thumb{width:40px;height:40px;border-radius:.375rem;object-fit:cover;flex-shrink:0;}',
    '.jv-pinspo__item-name{flex:1;font-size:.8125rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}',
    '.jv-pinspo__cat-group{display:flex;gap:.5rem;flex-shrink:0;}',
    '.jv-pinspo__cat-label{display:flex;align-items:center;gap:.25rem;font-size:.75rem;cursor:pointer;}',
    '.jv-pinspo__cat-label input{accent-color:var(--brand-primary,#c99591);}',
    '.jv-pinspo__remove-btn{background:none;border:none;cursor:pointer;color:#9a918a;padding:.25rem;border-radius:.25rem;display:flex;align-items:center;transition:color .15s;}',
    '.jv-pinspo__remove-btn:hover{color:var(--brand-primary-deep,#a9706c);}',
    '.jv-pinspo__item-state{font-size:.7rem;padding:.125rem .375rem;border-radius:99px;font-weight:600;}',
    '.jv-pinspo__item-state--uploading{background:#e8e0dc;color:#6b5c57;}',
    '.jv-pinspo__item-state--done{background:#e6f2ec;color:#1f6b46;display:flex;align-items:center;gap:.25rem;}',
    '.jv-pinspo__item-state--error{background:#fce8e8;color:#b32727;display:flex;align-items:center;gap:.25rem;}',
    '.jv-pinspo__actions{display:flex;align-items:center;gap:.75rem;margin-top:.875rem;flex-wrap:wrap;}',
    '.jv-pinspo__upload-btn{display:inline-flex;align-items:center;gap:.375rem;padding:.5rem 1.25rem;border-radius:var(--radius-pill,3rem);background:var(--brand-primary,#c99591);color:#fff;font-family:var(--font-heading-family,"Raleway",sans-serif);font-weight:600;font-size:.8125rem;letter-spacing:.03em;border:none;cursor:pointer;transition:background .15s;}',
    '.jv-pinspo__upload-btn:hover:not(:disabled){background:var(--brand-primary-deep,#a9706c);}',
    '.jv-pinspo__upload-btn:disabled{opacity:.5;cursor:not-allowed;}',
    '.jv-pinspo__status{font-size:.8125rem;line-height:1.5;}'
  ].join('');

  function injectStyles() {
    if (document.getElementById('jv-pinspo-styles')) return;
    var s = document.createElement('style');
    s.id = 'jv-pinspo-styles';
    s.textContent = STYLES;
    document.head.appendChild(s);
  }

  /* ── Public entry point ──────────────────────────────────────────── */
  function init(opts) {
    if (!opts || !opts.containerEl || !opts.bridgeCall) {
      console.warn('[jvPartyInspoUpload] containerEl and bridgeCall are required');
      return;
    }

    injectStyles();

    var container = opts.containerEl;
    var memberContext = opts.memberContext || 'bride';
    var targetMemberName = opts.targetMemberName || '';
    var targetMemberRole = opts.targetMemberRole || 'Party Member';
    var partyToken = opts.partyToken || null;
    var onSuccess = typeof opts.onSuccess === 'function' ? opts.onSuccess : function () {};
    var onComplete = typeof opts.onComplete === 'function' ? opts.onComplete : function () {};
    var bridgeCall = opts.bridgeCall;

    var items = []; // { file, objectUrl, category, state }

    container.innerHTML = '';
    container.className = (container.className || '') + ' jv-pinspo';

    /* Zone */
    var zone = document.createElement('div');
    zone.className = 'jv-pinspo__zone';
    zone.setAttribute('role', 'button');
    zone.setAttribute('tabindex', '0');
    zone.setAttribute('aria-label', 'Select inspiration images');
    // Warmer copy (2026-09-10, audit finding F-11 / Rule 20): "JPEG, PNG, HEIC, WebP" is
    // techy jargon on a bride-facing page. The real formats accepted (ALLOWED_MIME above)
    // are unchanged -- iPhone/Android photos and screenshots both already work, this only
    // rewords what she's told about it.
    zone.innerHTML = [
      '<p class="jv-pinspo__zone-label">Drag and drop images here</p>',
      '<p class="jv-pinspo__zone-sub">Photos from your phone or camera work perfectly, up to 10 MB each</p>',
      '<button type="button" class="jv-pinspo__pick-btn">' + SVG_ADD + ' Choose files</button>'
    ].join('');

    var fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = ALLOWED_MIME.join(',');
    fileInput.multiple = true;
    fileInput.style.display = 'none';
    fileInput.setAttribute('aria-hidden', 'true');

    /* Item list */
    var list = document.createElement('ul');
    list.className = 'jv-pinspo__list';

    /* Actions row */
    var actionsRow = document.createElement('div');
    actionsRow.className = 'jv-pinspo__actions';
    actionsRow.style.display = 'none';

    var uploadBtn = document.createElement('button');
    uploadBtn.type = 'button';
    uploadBtn.className = 'jv-pinspo__upload-btn';
    uploadBtn.textContent = 'Upload images';

    var statusEl = document.createElement('p');
    statusEl.className = 'jv-pinspo__status';
    statusEl.setAttribute('aria-live', 'polite');
    statusEl.setAttribute('role', 'status');

    actionsRow.appendChild(uploadBtn);
    actionsRow.appendChild(statusEl);

    container.appendChild(zone);
    container.appendChild(fileInput);
    container.appendChild(list);
    container.appendChild(actionsRow);

    /* ── Event: open file picker ── */
    zone.querySelector('.jv-pinspo__pick-btn').addEventListener('click', function (e) {
      e.stopPropagation();
      fileInput.click();
    });

    zone.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
    });

    /* ── Event: drag-and-drop ── */
    zone.addEventListener('dragover', function (e) { e.preventDefault(); zone.classList.add('is-drag-over'); });
    zone.addEventListener('dragleave', function () { zone.classList.remove('is-drag-over'); });
    zone.addEventListener('drop', function (e) {
      e.preventDefault();
      zone.classList.remove('is-drag-over');
      handleFiles(Array.from(e.dataTransfer.files || []));
    });

    fileInput.addEventListener('change', function () {
      handleFiles(Array.from(fileInput.files || []));
      fileInput.value = '';
    });

    /* ── Handle new files ── */
    function handleFiles(files) {
      files.forEach(function (file) {
        if (ALLOWED_MIME.indexOf(file.type) === -1) {
          showStatus('Skipped "' + file.name + '" — unsupported type.', true);
          return;
        }
        if (file.size > MAX_BYTES) {
          showStatus('Skipped "' + file.name + '" — file too large (max 10 MB).', true);
          return;
        }
        var item = { file: file, objectUrl: URL.createObjectURL(file), category: '', state: 'pending' };
        items.push(item);
      });
      renderList();
    }

    /* ── Render item list ── */
    function renderList() {
      list.innerHTML = '';
      items.forEach(function (item, idx) {
        var li = document.createElement('li');
        li.className = 'jv-pinspo__item';

        var thumb = document.createElement('img');
        thumb.className = 'jv-pinspo__thumb';
        thumb.src = item.objectUrl;
        thumb.alt = '';

        var nameSpan = document.createElement('span');
        nameSpan.className = 'jv-pinspo__item-name';
        nameSpan.title = item.file.name;
        nameSpan.textContent = item.file.name;

        var catGroup = document.createElement('span');
        catGroup.className = 'jv-pinspo__cat-group';

        ['Hair', 'Makeup'].forEach(function (cat) {
          var id = 'jv-pinspo-cat-' + idx + '-' + cat.toLowerCase();
          var label = document.createElement('label');
          label.className = 'jv-pinspo__cat-label';
          var radio = document.createElement('input');
          radio.type = 'radio';
          radio.name = 'jv-pinspo-cat-' + idx;
          radio.value = cat;
          radio.id = id;
          if (item.category === cat) radio.checked = true;
          radio.addEventListener('change', function () {
            if (radio.checked) items[idx].category = cat;
          });
          label.appendChild(radio);
          label.appendChild(document.createTextNode(cat));
          catGroup.appendChild(label);
        });

        var stateSpan = document.createElement('span');
        stateSpan.className = 'jv-pinspo__item-state jv-pinspo__item-state--' + item.state;
        if (item.state === 'uploading') stateSpan.textContent = 'Uploading…';
        else if (item.state === 'done') stateSpan.innerHTML = SVG_CHECK + ' Done';
        else if (item.state === 'error') {
          // 2026-09-14 fix: the server's real failure reason (rate limit, folder
          // access, unsupported type, too large) was computed correctly by
          // JV_partyInspo_upload but discarded on every failure path -- the bride
          // only ever saw a bare "Failed" with no explanation, so a genuine,
          // actionable error (e.g. "Too many uploads, wait a few minutes") looked
          // identical to a silent, unexplained block. Show the real reason as a
          // tooltip on the failed row (title attr keeps the compact layout intact);
          // the aggregate status line below also surfaces the first real error.
          stateSpan.innerHTML = SVG_WARN + ' Failed';
          if (item.error) stateSpan.title = item.error;
        }

        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'jv-pinspo__remove-btn';
        removeBtn.setAttribute('aria-label', 'Remove ' + item.file.name);
        removeBtn.innerHTML = SVG_TRASH;
        removeBtn.addEventListener('click', function () {
          URL.revokeObjectURL(item.objectUrl);
          items.splice(idx, 1);
          renderList();
        });

        if (item.state === 'uploading' || item.state === 'done') {
          removeBtn.disabled = true;
          removeBtn.style.opacity = '0.3';
        }

        li.appendChild(thumb);
        li.appendChild(nameSpan);
        li.appendChild(catGroup);
        li.appendChild(stateSpan);
        li.appendChild(removeBtn);
        list.appendChild(li);
      });

      actionsRow.style.display = items.length > 0 ? '' : 'none';
    }

    /* ── Upload all ── */
    uploadBtn.addEventListener('click', function () {
      var pending = items.filter(function (i) { return i.state === 'pending'; });

      if (pending.length === 0) {
        showStatus('No new images to upload.', false);
        return;
      }

      var missing = pending.filter(function (i) { return !i.category; });
      if (missing.length > 0) {
        showStatus('Please select Hair or Makeup for each image before uploading.', true);
        return;
      }

      uploadBtn.disabled = true;
      showStatus('Uploading…');
      uploadSequential(pending, 0, 0, []);
    });

    // Per-errorCode plain-language message + whether the bride should be
    // told to wait vs. fix something vs. just retry now (2026-09-17, real
    // structured error handling per the External GPT brief Jess approved,
    // BRIEFS/External GPT/Bridal Hub/Briefs/EXTERNAL-GPT-[FRONTEND-UI]-
    // bridal-hub-party-upload-experience-2026-09-17.md). Keyed on the
    // STABLE errorCode from JV_partyInspo_upload's response, never on
    // string-matching the .error text, so this stays correct even if the
    // server's wording changes later. "rate_limited" wording is
    // deliberately generic ("a lot of photos have been added to this
    // wedding recently") rather than claiming specific concurrent-user
    // presence data this page does not actually have.
    var ERROR_COPY = {
      auth_failed:            { text: "We couldn't confirm who you are, try reopening this page from your invite link.", retryable: 'never' },
      invalid_category:       { text: 'Please choose Hair or Makeup for this photo.', retryable: 'never' },
      unsupported_file_type:  { text: 'This file type is not supported. Try a JPEG, PNG, HEIC or WebP photo.', retryable: 'never' },
      no_content:              { text: 'This photo did not upload correctly, try selecting it again.', retryable: 'now' },
      file_too_large:          { text: 'This photo is too large (10 MB max). Try a smaller version.', retryable: 'never' },
      rate_limited:            { text: 'A lot of photos have been added to this wedding recently. Wait a few minutes and try again.', retryable: 'later' },
      folder_access_failed:    { text: "We couldn't reach your wedding's photo storage right now.", retryable: 'later' },
      unknown_error:           { text: 'Something went wrong on our end.', retryable: 'now' },
      network_error:           { text: 'Connection trouble, check your signal and try again.', retryable: 'now' }
    };

    function errorCopyFor_(result) {
      var code = (result && result.errorCode) || 'unknown_error';
      return ERROR_COPY[code] || { text: (result && result.error) || 'Upload failed.', retryable: 'now' };
    }

    function uploadSequential(pending, index, successes, failedItems) {
      if (index >= pending.length) {
        uploadBtn.disabled = false;
        var failures = failedItems.length;
        if (failures === 0) {
          showStatus(successes + ' image' + (successes !== 1 ? 's' : '') + ' uploaded.');
        } else if (successes === 0) {
          // Every upload failed. Show the first failure's real reason rather
          // than a blanket "please retry", it may not apply to all of them.
          showStatus(failures + ' photo' + (failures !== 1 ? 's' : '') + ' need attention: ' + failedItems[0].copy.text, true);
        } else {
          // Mixed batch (2026-09-17): plain count first, exact wording Jess
          // approved, never a blanket "retry all" when the failures have
          // different causes. Individual reasons live as a tooltip on each
          // failed row (renderList below), this line stays short.
          showStatus(successes + ' of ' + (successes + failures) + ' uploaded, ' + failures + ' need attention.', true);
        }
        // Clear done items from the queue, they now live in the persistent area.
        // Revoke their object URLs to release memory.
        items = items.filter(function (i) {
          if (i.state === 'done') { try { URL.revokeObjectURL(i.objectUrl); } catch (e) {} return false; }
          return true;
        });
        renderList();
        onComplete(successes, failures);
        return;
      }

      var item = pending[index];
      item.state = 'uploading';
      renderList();

      jvDownscaleImage(item.file, 2000, 0.85, function (scaledB64, scaledMime) {
        if (scaledB64) {
          doUpload(scaledB64, scaledMime);
        } else {
          fileToBase64(item.file, function (b64, mime) { doUpload(b64, mime); });
        }
      });

      function doUpload(base64Content, resolvedMime) {
        var payload = {
          memberContext: memberContext,
          targetMemberName: targetMemberName,
          targetMemberRole: targetMemberRole,
          category: item.category,
          filename: item.file.name,
          base64Content: base64Content,
          mimeType: resolvedMime || item.file.type
        };

        if (memberContext === 'partyMember' && partyToken) {
          payload.partyToken = partyToken;
        }

        bridgeCall('bridge_party_uploadInspoImage', payload).then(function (result) {
          if (result && result.ok) {
            item.state = 'done';
            // Optimistic thumbnail: server returns {pending:true, thumbUrl} on the
            // fast async path. Swap the list img to that Drive lh3 URL immediately
            // so the bride sees her photo land instantly. The CDN URL backfills on
            // the next hub load after the background drainer completes.
            if (result.thumbUrl && item.objectUrl) {
              item.objectUrl = result.thumbUrl;
            }
            // Pass full result so callers get thumbUrl + fileId, not just the
            // Drive view URL (fileUrl) which does not render as <img src>.
            onSuccess(item.category, result.fileUrl, result.savedAs, result);
            uploadSequential(pending, index + 1, successes + 1, failedItems);
          } else {
            item.state = 'error';
            item.copy = errorCopyFor_(result);
            item.error = item.copy.text;
            item.traceId = result && result.traceId;
            failedItems.push(item);
            uploadSequential(pending, index + 1, successes, failedItems);
          }
        }).catch(function () {
          item.state = 'error';
          item.copy = errorCopyFor_({ errorCode: 'network_error' });
          item.error = item.copy.text;
          failedItems.push(item);
          uploadSequential(pending, index + 1, successes, failedItems);
        });
      }
    }

    /* ── Helpers ── */
    function showStatus(msg, isError) {
      statusEl.textContent = msg || '';
      statusEl.style.color = isError ? 'var(--brand-error,#e53935)' : 'var(--brand-text,#2f2524)';
    }

    function fileToBase64(file, callback) {
      var reader = new FileReader();
      reader.onload = function (e) {
        var result = e.target.result;
        var base64 = result.split(',')[1] || result;
        callback(base64, file.type);
      };
      reader.readAsDataURL(file);
    }

    // Client-side downscale: cap longest edge at maxEdge px, re-encode as JPEG.
    // Skips non-raster types and files already under ~1.5 MB.
    // cb(base64, mimeType) -- base64 is null when downscale was skipped (caller uses fileToBase64).
    function jvDownscaleImage(file, maxEdge, quality, cb) {
      if (!/^image\/(jpe?g|png|webp)$/i.test(file.type) || file.size < 1.5 * 1024 * 1024) {
        return cb(null, file.type);
      }
      var objectUrl = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        URL.revokeObjectURL(objectUrl);
        var scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        var w = Math.round(img.width * scale);
        var h = Math.round(img.height * scale);
        var canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        var dataUrl = canvas.toDataURL('image/jpeg', quality);
        cb(dataUrl.split(',')[1], 'image/jpeg');
      };
      img.onerror = function () {
        URL.revokeObjectURL(objectUrl);
        cb(null, file.type);
      };
      img.src = objectUrl;
    }
  }

  /* ── Export ── */
  window.jvPartyInspoUpload_init = init;
})();
